import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { checkupAblaufAPI } from '@/lib/api';
import { CheckupStepperPanel } from '@/components/CheckupStepper';

/* Karte „Check-Up-Ergebnis“ (19.10.2026, Check-Up-Ablauf der Trainer-App, backend/checkup_ablauf.py). Nur lesen:
   die letzten Check-Ups mit Datum, Trainer, Antworten, Plan (Link /planer) und ob gemessen wurde. Messwerte liefert
   der Server nur mit dem Recht ernaehrung.view, Beschwerden/Notiz/Wettkampfdatum (Gesundheitsdaten) nur mit
   ernaehrung.view oder training.view — sonst steht dort „angegeben“. Gerechnet und gefiltert wird hier nichts.
   Ohne Ergebnis oder ohne Zugriff bleibt die Karte weg.
   Kursempfehlung (19.10.2026, backend/checkup_kurse.py): Termine, Hinweise für den Verkauf und Alternativen, wie der
   Server sie gerechnet hat.
   Kundenakte Schritt 3+4 (28.10.2026): bestätigte, noch offene Check-ups (`offen`) mit Knopf „Check-up durchführen“ — öffnet
   den Stepper als Vollbild-Panel (`CheckupStepperPanel`, derselbe wie am Studio-Tablet); dazu Befund, Empfehlung und was
   daraus wurde. Den Befundtext liefert der Server nur mit dem Recht Training oder Ernährung. */

const tagText = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');
const zahl = (v, stellen = 1) => (v == null ? '–' : Number(v).toFixed(stellen).replace('.', ','));
const PLAN_STATUS = { entwurf: 'Entwurf – wartet auf Freigabe', freigegeben: 'freigegeben', archiviert: 'archiviert' };
const QUELLE = { ki: 'KI-Vorschlag', vorlage: 'Vorlage' };
const AUSGANG = { buchen: 'gebucht', senden: 'Angebot gesendet', spaeter: 'später – Nachfassen', kein_bedarf: 'kein Bedarf' };

function Befund({ e }) {
  if (!e.befund && !e.empfehlung && !e.ergebnis) return null;
  const b = e.befund;
  return (
    <div className="text-xs space-y-0.5" data-testid="checkup-ergebnis-befund">
      {b && (
        <p><span className="text-muted-foreground">Befund: </span>{b.bewertung_label}{b.hauptziel_label ? ` · Ziel ${b.hauptziel_label}` : ''}
          {(b.huerden || []).length ? ` · Hürden: ${b.huerden.join(', ')}` : ''}</p>
      )}
      {b?.befund_text && <p className="whitespace-pre-line">{b.befund_text}</p>}
      {b?.befund_text_verborgen && <p className="text-muted-foreground">Befundtext angegeben (nur mit dem Recht Training oder Ernährung sichtbar)</p>}
      {e.empfehlung && (
        <p><span className="text-muted-foreground">Empfehlung: </span>
          {(e.empfehlung.produkte || []).length ? e.empfehlung.produkte.map((p) => p.titel).join(' + ') : 'keine'}</p>
      )}
      {e.ergebnis && <p className="font-medium"><span className="text-muted-foreground font-normal">Ergebnis: </span>{AUSGANG[e.ergebnis.ausgang] || e.ergebnis.ausgang}</p>}
    </div>
  );
}

function terminText(t) {
  return `${t.tag} ${t.start}${t.ende ? `–${t.ende}` : ''} · ${t.name}`;
}

function Kursempfehlung({ k }) {
  if (!k) return null;
  return (
    <div className="text-xs space-y-1" data-testid="checkup-ergebnis-kurse">
      <p className="text-muted-foreground">
        Kursempfehlung{k.anzahl != null ? ` · ${k.anzahl} pro Woche` : ''}
        {k.kontingent_woche != null ? ` (Tarif: ${k.kontingent_woche})` : ''}
      </p>
      {(k.termine || []).length > 0 ? (
        <ul className="space-y-0.5">
          {k.termine.map((t) => (
            <li key={t.kurs_id} className="flex flex-wrap gap-x-2">
              <span className="font-medium tabular-nums">{terminText(t)}</span>
              {t.grund ? <span className="text-muted-foreground">{t.grund}</span> : null}
            </li>
          ))}
        </ul>
      ) : <p>Keine Kurstermine.</p>}
      {k.uebernommen && (
        <p className="font-medium" data-testid="checkup-ergebnis-uebernommen">
          Vom Mitglied übernommen: {(k.uebernommen.termine || []).length ? k.uebernommen.termine.map(terminText).join(' · ') : 'keiner'}
          {k.uebernommen.von?.name ? ` (${k.uebernommen.von.name})` : ''}
        </p>
      )}
      {(k.hinweise || []).length > 0 && (
        <ul className="list-disc pl-4 space-y-0.5">
          {k.hinweise.map((h) => <li key={h}>{h}</li>)}
        </ul>
      )}
      {(k.alternativen || []).length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Weitere passende Kurse: {k.alternativen.map(terminText).join(' · ')}
        </p>
      )}
    </div>
  );
}

function Ergebnis({ e }) {
  const wann = e.tag ? tagText(e.tag) : (e.termin_text || tagText((e.erstellt_am || '').slice(0, 10)));
  return (
    <li className="py-2 space-y-1.5" data-testid="checkup-ergebnis">
      <p className="text-xs">
        <span className="font-medium tabular-nums">{wann}</span>
        <span className="text-muted-foreground">
          {e.trainer_name ? ` · ${e.trainer_name}` : ''}
          {e.abgeschlossen ? ' · abgeschlossen' : ' · läuft noch'}
        </span>
      </p>
      {e.antworten.length > 0 ? (
        <dl className="grid grid-cols-[minmax(0,40%)_1fr] gap-x-2 gap-y-0.5 text-xs">
          {e.antworten.map((a) => (
            <div key={a.key} className="contents">
              <dt className="text-muted-foreground truncate" title={a.frage}>{a.frage}</dt>
              <dd className="min-w-0 break-words">{a.antwort}</dd>
            </div>
          ))}
        </dl>
      ) : <p className="text-xs text-muted-foreground">Noch keine Antworten.</p>}
      <p className="text-xs" data-testid="checkup-ergebnis-plan">
        <span className="text-muted-foreground">Plan: </span>
        {e.plan ? (
          <>
            <Link to="/planer" className="underline underline-offset-2 hover:text-foreground">{e.plan.name}</Link>
            {` · ${PLAN_STATUS[e.plan.status] || e.plan.status}`}
            {e.plan.quelle ? ` · ${QUELLE[e.plan.quelle] || e.plan.quelle}` : ''}
            {e.plan.startdatum ? ` · ${tagText(e.plan.startdatum)}–${tagText(e.plan.enddatum)}` : ''}
          </>
        ) : 'noch keiner'}
      </p>
      <Kursempfehlung k={e.kursempfehlung} />
      <Befund e={e} />
      <p className="text-xs" data-testid="checkup-ergebnis-pt">
        <span className="text-muted-foreground">Personal Training: </span>
        {!e.pt ? 'noch offen'
          : e.pt.art === 'termin' ? `Termin ${tagText(e.pt.datum)} ${e.pt.start}–${e.pt.ende}`
            : 'Büro vereinbart den Termin (Aufgabe)'}
      </p>
      <p className="text-xs" data-testid="checkup-ergebnis-ernaehrung">
        <span className="text-muted-foreground">Ernährungscoaching: </span>
        {!e.ernaehrung_angebot ? 'noch offen' : e.ernaehrung_angebot.antwort === 'ja' ? 'Ja, Erstgespräch angefragt (Aufgabe)' : 'Nein'}
      </p>
      <p className="text-xs" data-testid="checkup-ergebnis-messung">
        <span className="text-muted-foreground">Körperanalyse: </span>
        {e.koerperanalyse_abgelehnt ? 'vom Mitglied abgelehnt'
          : !e.gemessen ? 'nicht gemessen'
            : e.messung ? `gemessen – ${zahl(e.messung.gewicht_kg)} kg, Muskelmasse ${zahl(e.messung.muskelmasse_kg)} kg, Körperfett ${zahl(e.messung.fett_prozent)} %`
              : 'gemessen'}
      </p>
    </li>
  );
}

export default function CheckupErgebnisKarte({ leadId }) {
  const [daten, setDaten] = useState(null);
  const [panel, setPanel] = useState(null);       // checkup_id des geöffneten Steppers
  const [stand, setStand] = useState(0);
  const lauf = useRef(0);

  useEffect(() => {
    const nr = ++lauf.current;
    setDaten(null);
    checkupAblaufAPI.mitglied(leadId)
      .then((r) => { if (nr === lauf.current) setDaten(r.data); })
      .catch(() => { if (nr === lauf.current) setDaten(null); });
    return () => { lauf.current += 1; };
  }, [leadId, stand]);

  const offen = daten?.offen || [];
  if (!daten || (!(daten.ergebnisse || []).length && !offen.length)) return null;
  return (
    <Card className="border-border/60" data-testid="checkup-ergebnis-karte">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-marke" aria-hidden="true" /> Check-Up-Ergebnis
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 text-sm">
        {offen.length > 0 && (
          <ul className="space-y-2 pb-2 border-b mb-1" data-testid="checkup-offen">
            {offen.map((o) => (
              <li key={o.checkup_id} className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs">
                  <span className="font-medium tabular-nums">{o.termin_text || 'Check-up'}</span>
                  <span className="text-muted-foreground">{o.trainer_name ? ` · ${o.trainer_name}` : ' · noch kein Trainer'}</span>
                </span>
                <Button size="sm" className="min-h-[44px]" onClick={() => setPanel(o.checkup_id)} data-testid={`checkup-starten-${o.checkup_id}`}>
                  {o.begonnen ? 'Check-up fortsetzen' : 'Check-up durchführen'}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <ul className="divide-y">
          {(daten.ergebnisse || []).map((e) => <Ergebnis key={e.id} e={e} />)}
        </ul>
        {!daten.mit_gesundheit && (
          <p className="text-[11px] text-muted-foreground mt-1">Beschwerden und Notizen sind Gesundheitsdaten – sichtbar mit dem Recht Training oder Ernährung.</p>
        )}
      </CardContent>
      {panel && <CheckupStepperPanel leadId={leadId} checkupId={panel} onClose={() => { setPanel(null); setStand((n) => n + 1); }} />}
    </Card>
  );
}
